import {Box} from '@mui/material'
import {DataGridPro} from '@mui/x-data-grid-pro'
import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {programLayout} from '../columns/ProgramsGridLayout'
import {ProgramsGroupingModel} from '../func/ProgramsGroupingModel'

interface ProgramsDataGridProps {
  data: any[]
  checkDate: string | null
  activeTab: string
  programName: string
  officeCode: string
  onResolvedDataChange?: (data: any[]) => void
  onRowSelectChange?: (ids: any[]) => void
  externalClearSignal?: number
}

const ProgramsDataGrid = (props: ProgramsDataGridProps) => {
  const {
    data,
    checkDate,
    activeTab,
    programName,
    officeCode,
    onRowSelectChange,
    externalClearSignal,
  } = props
  const [resolvedData, setResolvedData] = useState<any[]>([])
  const [selectedIds, setSelectedIds] = useState<any[]>([])
  const [restriction, setRestriction] = useState<string | null>(null) // 제한 알림 상태

  useEffect(() => {
    setSelectedIds([])
    onRowSelectChange?.([])
  }, [externalClearSignal])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resolved = await Promise.all(data)
        setResolvedData(resolved.flat().filter((item) => item !== undefined))
      } catch (err) {
        console.error('데이터로드실패', err)
      }
    }
    fetchData()
  }, [data])

  const excludeOffices = [
    'A800',
    '4700',
    '5701',
    'H101',
    'A600',
    '1900',
    '7601',
    '7602',
    '3801',
    '8601',
    '7001',
  ]

  const handleInputChange = (index: number, field: string, value: string) => {
    setResolvedData((prev) => {
      const updated = [...prev]
      const row = {...updated[index], [field]: value}

      const protectedChargeStatuses = ['BEF_ABNORMAL', 'BEF_DAY', 'AFT_DAY']
      const isProtected = protectedChargeStatuses.includes(row.charge_status)

      const usageDisabled = excludeOffices.includes(officeCode)
      const isEmptyInput = value === '' || value === null

      const checkValue =
        row.check_value === null || row.check_value === ''
          ? Number(row.check_value_default)
          : Number(row.check_value)

      const befCheckValue = Number(row.bef_check_value)

      if (!usageDisabled && !isEmptyInput) {
        const nowUsed = checkValue - befCheckValue

        row.now_used = nowUsed

        if (officeCode === 'Z200') {
          const averageUsed = Number(row.now_check_quantity)

          row.preview_leak_status =
            !isNaN(nowUsed) && !isNaN(averageUsed) && nowUsed >= averageUsed * 1.2
        } else {
          const befUsed = Number(row.bef_used)

          row.preview_leak_status = !isNaN(nowUsed) && !isNaN(befUsed) && nowUsed >= befUsed * 1.2
        }

        row.isDirty = true

        if (!isProtected) {
          if (!isNaN(nowUsed) && nowUsed < 0) {
            row.charge_status = 'MINUS'
          } else {
            row.charge_status = 'NORMAL'
          }
        }
      } else {
        row.now_used = ''
        row.preview_leak_status = false
        row.isDirty = true
      }

      row.edited_status = true
      row.process_status = 'FIX'

      updated[index] = row

      return updated
    })
  }

  const columns = programLayout(handleInputChange, activeTab, programName, officeCode).map(
    (col) => ({
      ...col,
      sortable: ['customer_number', 'edited_status'].includes(col.field),
    })
  )

  const getRowColor = (row: any) => {
    if (row?.edited_status === true) return 'edited-row'
    return ''
  }

  useEffect(() => {
    props?.onResolvedDataChange?.(resolvedData)
  }, [resolvedData])

  const reasonMap: Record<string, string> = {
    MINUS: '사용량 음수',
    NO_BEF: '전월 검침 정보 확인 불가',
    BEF_ABNORMAL: '전월 상태가 유효하지 않음',
    // AFT_DAY: '등록 검침일보다 초과',
    NO_CURR: '당월 검침 정보 확인 불가',
    DEFAULT: '검침 상태가 유효하지 않음',
  }

  return (
    <Box className='bg-gray-50 rounded-lg shadow-md h-full'>
      <DataGridPro
        rows={resolvedData.map((row: any, index: number) => ({id: index, ...row}))}
        columns={columns}
        className='border border-gray-300'
        rowHeight={110}
        components={{Footer: () => null}}
        localeText={{
          noRowsLabel: checkDate
            ? '조회된 데이터가 없습니다.'
            : '납기일자를 선택한 후 조회를 눌러주세요.',
        }}
        headerHeight={30}
        disableColumnMenu
        columnGroupingModel={ProgramsGroupingModel(programName, activeTab)}
        experimentalFeatures={{columnGrouping: true}}
        checkboxSelection={activeTab === '확인필요'}
        disableSelectionOnClick={true} // ✅ 기본 클릭 선택 비활성화
        selectionModel={selectedIds} // ✅ 상태 직접 연결
        onSelectionModelChange={(newSelection) => {
          setSelectedIds(newSelection)
          onRowSelectChange?.(newSelection)
        }}
        isRowSelectable={(params) =>
          activeTab !== '확인필요' ||
          (params.row.charge_status === 'ABNORMAL' && params.row.process_status === 'DIGIT') ||
          params.row.charge_status === 'BEF_DAY' ||
          params.row.charge_status === 'AFT_DAY' ||
          params.row.process_status === 'DANGER'
        }
        onRowClick={(params, event) => {
          const isSelectable =
            activeTab !== '확인필요' ||
            (params.row.charge_status === 'ABNORMAL' && params.row.process_status === 'DIGIT') ||
            params.row.charge_status === 'BEF_DAY' ||
            params.row.charge_status === 'AFT_DAY' ||
            params.row.process_status === 'DANGER'

          if (!isSelectable) {
            event.stopPropagation()
            const reason = reasonMap[params.row.charge_status] || reasonMap.DEFAULT
            setRestriction(`이동불가: ${reason}`)
            setTimeout(() => setRestriction(null), 3000)
            return
          }

          // ✅ 직접 선택 토글
          const id = params.id
          setSelectedIds((prev) => {
            const alreadySelected = prev.includes(id)
            const updated = alreadySelected ? prev.filter((i) => i !== id) : [...prev, id]
            onRowSelectChange?.(updated)
            return updated
          })
        }}
        getRowClassName={(params) => {
          const row = params?.row
          return `${getRowColor(row)} clickable-row`
        }}
        initialState={{
          pinnedColumns: {left: ['__check__', 'check_date', 'customer_number']},
          sorting: {sortModel: [{field: 'customer_number', sort: 'asc'}]},
        }}
        sx={{
          '& .MuiDataGrid-root, & .MuiDataGrid-columnHeaders,': {
            fontFamily: 'Noto Sans KR, sans-serif ',
          },
          '& .MuiDataGrid-columnHeaders': {backgroundColor: '#f3f4f6'},
          '& .MuiDataGrid-cell': {fontSize: '16px', fontWeight: '400'},
          '& .MuiDataGrid-pinnedColumns': {backgroundColor: '#f9fafb'},
          '& .MuiDataGrid-pinnedColumnHeaders': {backgroundColor: '#f3f4f6'},
          '& .edited-row': {backgroundColor: '#e9f7ef'},
          '& .MuiDataGrid-row.warning-row': {backgroundColor: '#fef9e7'},
          '& .MuiDataGrid-cell:focus': {outline: 'none'},
          '& .MuiDataGrid-cell:focus-within': {outline: 'none'},
          // ✅ 비고 옆 여백 클릭 가능하게 확장
          '& .clickable-row': {position: 'relative'},
          '& .clickable-row::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: '100%', // 마지막 셀 끝부터
            width: '9999px',
            cursor: 'pointer',
          },
        }}
      />

      {restriction &&
        createPortal(
          <div className='fixed top-[10%] left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-md text-lg z-50'>
            {restriction}
          </div>,
          document.body
        )}
    </Box>
  )
}

export default ProgramsDataGrid
